/* eslint-disable react-hooks/exhaustive-deps */
import { SyncOutlined } from '@ant-design/icons'
import {
  Button,
  DatePicker,
  Form,
  FormInstance,
  InputNumber,
  notification,
  Select,
  Tooltip,
} from 'antd/es'
import { useWatch } from 'antd/es/form/Form'
import TextArea from 'antd/es/input/TextArea'
import dayjs, { Dayjs } from 'dayjs'
import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { QUERY_KEYS } from '@api'
import { ASSIGNATION_OPTIONS, CONTRACT_LINE_FIELD_NAME, INCOTERM_GROUPS } from '@constants'
import { useUser } from '@contexts'
import {
  useCarriers,
  useDestinations,
  useEquipment,
  useMaterials,
  usePurchaseOrdersByMaterial,
  // useSimulationTicketAdmin,
  useSuppliers,
} from '@hooks'
import {
  AssignationValues,
  ComboOption,
  // ContractByCarrier,
  Inco1Type,
  RoleEnum,
  SapItemSupplier,
  Supplier,
  Ticket,
  TripTypeEnum,
} from '@types'
import { CutoffTimeUtils, OtherUtils, StringUtils } from '@utils'

import AssignationRadioButton from '../../../../components/AssignationRadioButton'
import { FlexCol, FlexRow, StyledRow } from '../../../../components/StyledComponents'
import { DATE_KEYS } from '../../../../constants/dateKeys'
import { mapDestinationsToCombo } from '../../../../utils/array-entity-to-combo'
import { limitIntEntry, preventEvent } from '../../../../utils/inputs'
import { useGetContractsByCarrier } from '../../../../hooks/useGetContractsByCarrier'

// TIPOS
type Dates = Date[] | undefined

// INTERFACES
interface TicketFormProps {
  form: FormInstance<any>
  handlePrimaryButtonDisabling: (value: boolean) => void
  dates: Dates
  setDates: Dispatch<SetStateAction<Dates>>
  roles: RoleEnum[]
  isWarning: boolean | undefined
  setIsWarning: Dispatch<SetStateAction<boolean | undefined>>
  ifToApproved: boolean | undefined
  assignValue: AssignationValues | undefined
  defaultAssignValue: AssignationValues
  setAssignValue: Dispatch<SetStateAction<AssignationValues | undefined>>
  carrierAvailabilityIsEnabled: boolean
  ticket?: Ticket
  maxAllowedDaysForSupplierRequest?: number
  cutoffTime: string
}

// CONSTANTES
const { classesToString } = StringUtils

const { PURCHASES } = QUERY_KEYS

const initialValuesNewTicket = {
  count: 1,
  supplierId: undefined,
  depotId: undefined,
  materialId: undefined,
  equipmentId: undefined,
  destinationId: undefined,
  ticketsDate: dayjs(
    `${dayjs().add(1, 'd').format('DD-MM-YYYY')} ${dayjs().hour() + 1}:00:00`,
    DATE_KEYS.FORMAT.FULL_TIME,
  ).toDate(),
  carrierId: undefined,
  comments: undefined,
  approved: false,
  documentNumberId: undefined,
  tripType: undefined,
}

const handleFilterOptions = (input: string, option: any) => {
  if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
  return true
}

const today = dayjs()

const AddTicketFormAdmin: FC<PropsWithChildren<TicketFormProps>> = ({
  handlePrimaryButtonDisabling,
  dates,
  setDates,
  roles,
  isWarning,
  setIsWarning,
  form,
  assignValue,
  setAssignValue,
  defaultAssignValue,
  cutoffTime,
}) => {
  // hooks
  const { t } = useTranslation()
  const { state: user } = useUser()
  const queryClient = useQueryClient()
  const { allCarriersOptions } = useCarriers({ disabled: false, limit: 400 })
  const { equipments } = useEquipment()
  const { suppliersOptions, suppliers } = useSuppliers({ disabled: false })
  /* const {
    simulate: simulateTicket,
    isLoading: isSimulating,
    options: alternativeOptions,
    suggested,
    alternatives,
    resetSimulation,
  } = useSimulationTicketAdmin() */
  const {
    contractsByCarrier,
    getContractsByCarrier,
    getContractsByCarrierIsLoading,
    resetContractsByCarrier,
  } = useGetContractsByCarrier()

  // Estado para controlar si se ha ejecutado la simulación
  const [hasSimulationExecuted, setHasSimulationExecuted] = useState<boolean>(false)

  // HOOKS DE ESTADOS (useState)
  const [plantId, setPlantId] = useState<string>()
  const [depots, setDepots] = useState<ComboOption[]>([])
  const [isCalendarVisible, setIsCalendarVisible] = useState<boolean>(false)
  const [beforeToday, setBeforeToday] = useState<boolean>()
  const [required, setRequired] = useState<boolean>(true)
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier>()
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<SapItemSupplier>()
  const [enableDocument, setEnableDocument] = useState<boolean>(false)
  //const [selectedSapItemId, setSelectedSapItemId] = useState<number>()
  const [selectedCarrier, setSelectedCarrier] = useState<number>()
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<number>()
  //const [hasSuccessfulSimulation, setHasSuccessfulSimulation] = useState<boolean>(false)

  const currentContract = useWatch(CONTRACT_LINE_FIELD_NAME, form)

  // hooks with dependencies
  const { purchaseOrders } = usePurchaseOrdersByMaterial(
    user?.userDetail.supplier?.sapId || selectedSupplier?.sapId,
  )
  const { materials, materialsOptionsSapCode } = useMaterials(
    selectedPurchaseOrder?.DocumentNumberID,
    'BLOQUEADA (Saldo Insuficiente) - ',
  )

  const destinationCodesFromPurchaseOrders = useMemo(
    () => [...new Set(purchaseOrders?.map(({ PlantID }) => PlantID))],
    [purchaseOrders],
  )
  const purchaseOrdersFilteredByPlant = useMemo(
    () => (plantId ? purchaseOrders?.filter(({ PlantID }) => PlantID === plantId) : purchaseOrders),
    [plantId, purchaseOrders],
  )
  const currentSupplierSapId = selectedSupplier?.sapId || user?.userDetail.supplier?.sapId
  const { allDestinationsOptions, destinations } = useDestinations(currentSupplierSapId)
  const tripTypeValue = useWatch('tripType', form) as TripTypeEnum
  const isTripCfr = useMemo(() => tripTypeValue === TripTypeEnum.CFR, [tripTypeValue])

  const isRequiredMsg = t('globals.obligatory_field')

  const isCarrierDisabled = useMemo(() => {
    return (
      assignValue === 'manual' &&
      tripTypeValue !== TripTypeEnum.FOB &&
      tripTypeValue !== TripTypeEnum.TRANSFERENCE
    )
  }, [assignValue, tripTypeValue])

  const isCarrierRequired = useMemo(() => {
    return (
      assignValue === 'manual' &&
      (tripTypeValue === TripTypeEnum.FOB || tripTypeValue === TripTypeEnum.TRANSFERENCE)
    )
  }, [assignValue, tripTypeValue])

  const isAutomaticDisabled = useMemo(() => {
    const carrierId = form.getFieldValue('carrierId')
    return !!carrierId
  }, [form])

  /* const selectedSapItemData = useMemo(() => {
    if (!selectedSapItemId) {
      return undefined
    }
    return (
      [...(suggested || []), ...(alternatives || [])]?.find(
        alternative => alternative?.id === selectedSapItemId,
      ) || undefined
    )
  }, [selectedSapItemId , suggested, alternatives ]) */

  const closeCalendar = () => {
    setIsCalendarVisible(false)
  }

  const resetFields = () => {
    setTimeout(form.resetFields, 1)
  }

  const resetPurchaseOrders = () => {
    form.resetFields(['depotId', 'materialId', 'destinationId'])
    form.setFieldsValue({ documentNumberId: undefined, destinationId: undefined })
    setPlantId(undefined)
    setSelectedPurchaseOrder(undefined)
    queryClient.removeQueries([PURCHASES])
  }

  const handleSelectEquipment = (value?: number) => {
    setSelectedEquipmentId(value)
    if (value && assignValue === 'manual') {
      setAssignValue('manual')
    }
    if (initialValues.carrierId) {
      handlePrimaryButtonDisabling(!value)
    }
  }

  const handleSelectCarrier = (carrierId: number | undefined) => {
    setSelectedCarrier(carrierId)
    form.setFieldsValue({ carrierId })
  }

  const initialValues = useMemo(
    () => ({
      ...initialValuesNewTicket,
      autoAssignValue: assignValue,
    }),
    [assignValue],
  )

  const handleDestinationSelect = useCallback(
    (value: number) => {
      handlePrimaryButtonDisabling(true)
      form.setFieldsValue({ materialId: undefined })
      form.setFieldsValue({ documentNumberId: undefined })
      const destinationPlantId =
        destinations && destinations.find(destination => destination.id === value)
      const filteredPlantId = destinationPlantId?.plantId
      setPlantId(filteredPlantId)
    },
    [destinations],
  )

  const handleContractOnChange = (value: string) => {
    form.setFieldsValue({ [CONTRACT_LINE_FIELD_NAME]: value, sapItemId: undefined })
  }

  const isDateAfterToday = useCallback(
    () => dates && dates[0].getTime() < dayjs().toDate().getTime(),
    [dates],
  )

  const calendarFooter = useCallback(
    () => (
      <FlexRow
        style={{
          justifyContent: 'end',
          padding: '0.5rem',
        }}
      >
        <Button type="primary" onClick={closeCalendar}>
          Aceptar
        </Button>
      </FlexRow>
    ),
    [roles],
  )

  const handleMaterialsOptions = useCallback(
    (value: number | undefined) => {
      form.setFieldsValue({ materialId: value })
      handlePrimaryButtonDisabling(false)
    },
    [materials],
  )
  const applyCutoffTime = useCallback(
    (value: string, inputDates?: Date[]) =>
      inputDates?.map(date => CutoffTimeUtils.applyCutoffTimeToDate(date, value)),
    [],
  )

  const handleTimeChange = () => {
    const nextDates = applyCutoffTime(cutoffTime, dates)
    if (nextDates) {
      setDates(nextDates)
    }

    if (dates?.length && isDateAfterToday()) {
      setAssignValue('manual')
      setBeforeToday(true)
    } else {
      setBeforeToday(false)
    }
  }

  const handleChangeDate = useCallback(
    (thisDates?: Dayjs[]) => {
      const isCfr = tripTypeValue === TripTypeEnum.CFR || isTripCfr

      const sorted = thisDates?.map(d => d.toDate()).sort((a, b) => (a < b ? -1 : 1))
      const datesWithCutoff = applyCutoffTime(cutoffTime, sorted)
      setDates(datesWithCutoff)

      if (thisDates) {
        form.setFieldsValue({ withdrawalDates: datesWithCutoff })
      }

      if (isCfr) {
        handlePrimaryButtonDisabling(false)
      }
    },
    [tripTypeValue, isTripCfr, cutoffTime, applyCutoffTime],
  )

  const handleSupplierIdInput = useCallback(
    (value: number | undefined) => {
      resetPurchaseOrders()
      form.resetFields()
      if (value) {
        form.setFieldsValue({
          supplierId: value,
          documentNumberId: undefined,
          destinationId: undefined,
          materialId: undefined,
        })

        setAssignValue(defaultAssignValue)
        setPlantId(undefined)

        const supplierAux = suppliers?.find(supplier => supplier.id === value)
        setSelectedSupplier(supplierAux)

        const depotsAux = supplierAux?.depots.map(depot => ({
          label: depot.tag,
          value: depot.id,
        }))
        setDepots(depotsAux || [])
        setEnableDocument(!!supplierAux?.sapId)
        setDates([])
      }
      //setHasSuccessfulSimulation(false)
    },
    [suppliers],
  )

  const clearDates = () => {
    setDates([])
    form.setFieldValue('withdrawalDates', [])
  }

  const handlePurchaseDocumentChange = useCallback(
    (documentNumberId?: string) => {
      const order = purchaseOrders?.find(po => po.DocumentNumberID === documentNumberId)
      if (!order) {
        form.setFieldsValue({ documentNumberId: undefined, tripType: undefined })
        setSelectedPurchaseOrder(undefined)
        setDates([])
        form.resetFields(['withdrawalDates'])
        return
      }

      const newTripType = INCOTERM_GROUPS.FOB.includes(order.Inco1 as any)
        ? TripTypeEnum.FOB
        : INCOTERM_GROUPS.CFR.includes(order.Inco1 as any)
        ? TripTypeEnum.CFR
        : TripTypeEnum.TRANSFERENCE

      if (tripTypeValue && newTripType !== tripTypeValue) {
        setDates([])
        form.resetFields(['withdrawalDates'])
      }

      setSelectedPurchaseOrder(order)
      form.setFieldsValue({
        documentNumberId,
        tripType: newTripType,
      })

      handlePrimaryButtonDisabling(true)
    },
    [purchaseOrders, tripTypeValue, form, handlePrimaryButtonDisabling, setDates],
  )

  const TOOLTIP_TEXTS: Record<TripTypeEnum, string> = {
    [TripTypeEnum.FOB]: t('selected_Incoterm.fob'),
    [TripTypeEnum.CFR]: t('selected_Incoterm.cfr'),
    [TripTypeEnum.TRANSFERENCE]: '',
  }

  const getTooltipText = (tripType?: TripTypeEnum) =>
    TOOLTIP_TEXTS[tripType || TripTypeEnum.TRANSFERENCE] || ''

  const TripTypeDisplay: React.FC<{ tripType?: TripTypeEnum }> = ({ tripType }) => {
    const displayValue = tripType ? t(`trip_type.${tripType}`) : ''
    return (
      <Tooltip title={getTooltipText(tripType)}>
        <span className="ticket-field-value">{displayValue}</span>
      </Tooltip>
    )
  }

  // Función para obtener datos de simulación
  const handleSearchContractPosition = useCallback(() => {
    /* if (
      !selectedCarrier ||
      !form.getFieldValue('depotId') ||
      !form.getFieldValue('destinationId')
    ) {
      notification.error({
        message: t('globals.generic_error'),
        description: 'Faltan datos requeridos para la simulación',
      })
      return
    }
    simulateTicket({
      depotId: form.getFieldValue('depotId'),
      destinationId: form.getFieldValue('destinationId'),
      carrierId: selectedCarrier,
    }) */
    if (!selectedCarrier) {
      notification.error({
        message: t('globals.generic_error'),
        description: 'Faltan datos requeridos para obtener los contratos',
      })
      return
    }
    getContractsByCarrier(
      { carrierId: selectedCarrier },
      {
        onSuccess: data => {
          // Marcar que se ha ejecutado la simulación
          setHasSimulationExecuted(true)
          // Solo aplicar el color gris oscuro si la simulación fue exitosa y devolvió contratos
          // Si devuelve un array vacío, mantener el color original (gris)
          if (data && data.length > 0) {
            // Simulación exitosa con contratos - aplicar color gris oscuro
            // No establecer automáticamente los valores para que se muestre el placeholder
            // Los campos se mantendrán vacíos para mostrar "Seleccione una opción"
            // CÓDIGO COMENTADO - Establecer automáticamente los valores de la posición [0]
            // const sapItemIdValue = form.getFieldValue('sapItemId')
            // if (!sapItemIdValue) {
            //   form.setFieldValue({
            //     [CONTRACT_LINE_FIELD_NAME]: data?.[0]?.contract,
            //     sapItemId: data?.[0]?.contract_line?.[0]?.id,
            //   })
            // }
          } else {
            // Simulación no exitosa (array vacío) - mantener color original (gris)
            setHasSimulationExecuted(false)
          }
        },
      },
    )
  }, [selectedCarrier])

  useEffect(() => {
    if (isTripCfr !== undefined) {
      setAssignValue(isTripCfr ? 'manual' : assignValue)
    }
  }, [isTripCfr, assignValue])

  useEffect(() => {
    if (!dates) {
      setDates(undefined)
    }
    setPlantId(undefined)
    setDepots([])
    setRequired(true)
    setEnableDocument(false)
    resetFields()
  }, [])

  useEffect(() => {
    if (!!dates?.length) {
      setIsWarning(false)
      handleTimeChange()
    }
    form.setFieldsValue({
      withdrawalDates: dates,
    })
  }, [dates, cutoffTime])

  useEffect(() => {
    if (selectedPurchaseOrder) {
      form.setFieldsValue({ materialId: undefined })

      const value = INCOTERM_GROUPS.FOB.includes(selectedPurchaseOrder?.Inco1 as Inco1Type)
        ? TripTypeEnum.FOB
        : INCOTERM_GROUPS.CFR.includes(selectedPurchaseOrder?.Inco1 as Inco1Type)
        ? TripTypeEnum.CFR
        : undefined

      form.setFieldsValue({ tripType: value })
    }
  }, [selectedPurchaseOrder])

  useEffect(() => {
    if (!selectedSupplier?.sapId) {
      form.setFieldsValue({ tripType: TripTypeEnum.TRANSFERENCE })
    } else {
      form.setFieldsValue({ tripType: undefined })
    }
  }, [selectedSupplier])

  useEffect(() => {
    if (tripTypeValue === TripTypeEnum.CFR) {
      const currentValue = form.getFieldValue('count')
      if (currentValue !== 1) {
        form.setFieldsValue({ count: 1 })
      }

      if (dates && dates.length > 1) {
        const singleDate = [dates[0]]
        setDates(singleDate)
        form.setFieldsValue({
          withdrawalDates: singleDate,
        })
      }
    }
  }, [tripTypeValue, form, dates])

  useEffect(() => {
    if (isAutomaticDisabled && assignValue === 'automatic') {
      setAssignValue('manual')
    }
  }, [isAutomaticDisabled, assignValue, setAssignValue])

  useEffect(() => {
    if (tripTypeValue === TripTypeEnum.FOB) {
      setAssignValue(defaultAssignValue)
    }
  }, [tripTypeValue, defaultAssignValue, setAssignValue])

  useEffect(() => {
    if (assignValue === 'automatic' && tripTypeValue === TripTypeEnum.FOB) {
      form.setFieldsValue({ carrierId: undefined })
      setAssignValue('automatic')
    }
  }, [assignValue, tripTypeValue, form, setAssignValue])

  /* useEffect(() => {
    if (!isTripCfr) {
      if (!!alternativeOptions?.length) {
        setSelectedSapItemId((alternativeOptions[0]?.value as number) || undefined)
      } else {
        setSelectedSapItemId(undefined)
      }
    }
  }, [alternativeOptions]) */

  /* useEffect(() => {
    form.setFieldsValue({
      [CONTRACT_LINE_FIELD_NAME]: selectedSapItemData?.contract,
      sapItemId: selectedSapItemData?.id,
    })
  }, [selectedSapItemData]) */

  /* const depotId = useWatch('depotId', form)
  const destinationId = useWatch('destinationId', form) */

  const clearContractFields = useCallback(() => {
    //setSelectedSapItemId(undefined)
    //setHasSuccessfulSimulation(false)
    resetContractsByCarrier()
    //resetSimulation()
    setHasSimulationExecuted(false)
    form.setFieldsValue({
      [CONTRACT_LINE_FIELD_NAME]: null,
      sapItemId: null,
    })
  }, [
    /* resetSimulation, setSelectedSapItemId, */ resetContractsByCarrier,
    form,
    setHasSimulationExecuted,
  ])

  useEffect(() => {
    if (!isTripCfr) {
      clearContractFields()
    }
  }, [selectedCarrier /* , depotId, destinationId */])

  // Detectar cuando la simulación se complete exitosamente
  /* useEffect(() => {
    if (alternativeOptions && alternativeOptions.length > 0 && !isSimulating) {
      setHasSuccessfulSimulation(true)
    }
  }, [alternativeOptions, isSimulating]) */

  return (
    <Form
      form={form}
      initialValues={initialValues}
      name="ticket-form"
      className={`ticket-form ${hasSimulationExecuted ? 'simulation-executed' : ''}`}
    >
      <FlexCol className="ticket-form__column ticket-data">
        <FlexCol className="form-section">
          {/* CANTIDAD */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.quantity')}
              name="count"
              initialValue={tripTypeValue === TripTypeEnum.CFR ? 1 : undefined}
              rules={[
                {
                  required: true,
                  message: isRequiredMsg,
                },
                ...(tripTypeValue === TripTypeEnum.CFR
                  ? [
                      {
                        validator: (_: unknown, value: number) =>
                          value === 1
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(
                                  t('suppliers_router.new_ticket_modal.cfr_quantity_error'),
                                ),
                              ),
                      },
                    ]
                  : []),
              ]}
            >
              <>
                <InputNumber
                  onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => preventEvent(event)}
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => limitIntEntry(event)}
                  min={1}
                  max={10}
                  disabled={tripTypeValue === TripTypeEnum.CFR}
                  style={{ width: '100%' }}
                  value={form.getFieldValue('count')}
                />
              </>
            </Form.Item>
          </StyledRow>
          {/* PROVEEDOR */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.supplier')}
              name="supplierId"
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
            >
              <Select
                allowClear
                showSearch
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={suppliersOptions}
                onChange={handleSupplierIdInput}
                filterOption={handleFilterOptions}
              />
            </Form.Item>
          </StyledRow>
          {/* ORIGEN */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.origin')}
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
              name="depotId"
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={depots}
              />
            </Form.Item>
          </StyledRow>
          {/* DESTINO */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.destination')}
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
              name="destinationId"
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                allowClear
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={
                  !!selectedSupplier?.sapId
                    ? mapDestinationsToCombo(
                        destinations?.filter(destination =>
                          destinationCodesFromPurchaseOrders.includes(destination.plantId),
                        ),
                      )
                    : allDestinationsOptions
                }
                onChange={handleDestinationSelect}
              />
            </Form.Item>
          </StyledRow>
          {/* ORDEN DE COMPRA */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('purchase_order')}
              name="documentNumberId"
              rules={[
                {
                  required: enableDocument,
                  message: isRequiredMsg,
                },
              ]}
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                disabled={!tripTypeValue && (!enableDocument || !plantId)}
                placeholder={!!selectedSupplier?.sapId && t('globals.placeholder_select')}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                options={purchaseOrdersFilteredByPlant?.map(
                  ({ DocumentNumberID, ProposedDeliveryDate, Inco1, Inco2 }) => ({
                    label: `${DocumentNumberID} - ${OtherUtils.capitalize(
                      dayjs(ProposedDeliveryDate).format('MMM-YY'),
                    )} - ${Inco1}/${Inco2}`,
                    value: DocumentNumberID,
                  }),
                )}
                onChange={handlePurchaseDocumentChange}
              />
            </Form.Item>
          </StyledRow>
          {/* TIPO VIAJE */}
          {tripTypeValue === TripTypeEnum.TRANSFERENCE ? (
            <StyledRow className="new-ticket-form-row">
              <Form.Item label="Incoterm" name="tripType">
                <TripTypeDisplay tripType={TripTypeEnum.TRANSFERENCE} />
              </Form.Item>
            </StyledRow>
          ) : (
            <StyledRow className="new-ticket-form-row">
              <Form.Item
                label="Incoterm"
                rules={[{ required, message: isRequiredMsg }]}
                name="tripType"
              >
                <TripTypeDisplay tripType={tripTypeValue} />
              </Form.Item>
            </StyledRow>
          )}
          {/* MATERIAL */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.material')}
              name="materialId"
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
            >
              <Select
                showSearch
                onChange={handleMaterialsOptions}
                filterOption={handleFilterOptions}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={materialsOptionsSapCode}
              />
            </Form.Item>
          </StyledRow>
          {/* EQUIPO */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.equipment')}
              name="equipmentId"
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
            >
              <Select
                showSearch
                filterOption={handleFilterOptions}
                allowClear
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                placeholder={t('globals.placeholder_select')}
                options={equipments?.map(equipment => ({
                  label: equipment.nameShort,
                  value: equipment.id,
                }))}
                onChange={handleSelectEquipment}
              />
            </Form.Item>
          </StyledRow>
          {/* FECHA */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              label={
                <>
                  {required && <div className="required-asterisk">*</div>}
                  <span>{t('suppliers_router.new_ticket_modal.date')}</span>
                </>
              }
              rules={[
                {
                  required,
                  message: isRequiredMsg,
                },
              ]}
            >
              <FlexCol className="ticket-form__dates-column">
                <FlexRow>
                  <DatePicker
                    multiple={tripTypeValue !== TripTypeEnum.CFR}
                    onChange={date => {
                      if (!date) {
                        clearDates()
                        return
                      }

                      const thisDates = Array.isArray(date) ? date : [date]
                      handleChangeDate(thisDates as Dayjs[])
                    }}
                    open={isCalendarVisible}
                    renderExtraFooter={calendarFooter}
                    onClick={() => {
                      setIsCalendarVisible(true)
                    }}
                    value={
                      tripTypeValue !== TripTypeEnum.CFR
                        ? dates?.map(date => dayjs(date))
                        : dates?.length
                        ? dayjs(dates[0])
                        : undefined
                    }
                    maxTagCount="responsive"
                    required
                    allowClear
                    format={DATE_KEYS.FORMAT.DAY_SHORT}
                    className={classesToString([
                      'ticket-form__date-picker-input',
                      isWarning && 'ticket-form__is-warning',
                    ])}
                    minDate={isTripCfr ? today : undefined}
                  />
                </FlexRow>
                <FlexRow>
                  {!!dates?.length
                    ? `${t('suppliers_router.new_ticket_modal.dates_quantity')} ${dates?.length}`
                    : ''}
                </FlexRow>
              </FlexCol>
            </Form.Item>
          </StyledRow>
          {/* HORARIO DE CORTE */}
          {!isTripCfr && (
            <StyledRow className="new-ticket-form-row cutoff-time-info-row">
              <span className={classesToString([isWarning && 'ticket-form__is-warning'])}>
                {t('globals.cutoff_time_info', { time: cutoffTime })}
              </span>
            </StyledRow>
          )}
          {/* TRANSPORTISTA */}
          <StyledRow className="new-ticket-form-row">
            <Form.Item
              name="carrierId"
              label={t('suppliers_router.new_ticket_modal.carrier')}
              rules={[
                {
                  required: isCarrierRequired,
                  message: isRequiredMsg,
                },
              ]}
            >
              <Select
                showSearch
                placeholder={t('globals.placeholder_select')}
                filterOption={handleFilterOptions}
                onChange={handleSelectCarrier}
                disabled={assignValue === 'automatic' || isCarrierDisabled}
                getPopupContainer={(triggerNode: HTMLElement) =>
                  triggerNode.parentNode as HTMLElement
                }
                options={allCarriersOptions}
              />
            </Form.Item>
          </StyledRow>
          {/* ASIGNACION */}
          <StyledRow className="new-ticket-form-row" id="radio-button">
            <Form.Item
              label={t('suppliers_router.new_ticket_modal.assignment')}
              name="autoAssignValue"
            >
              <FlexRow className="ticket-form__assign-radio-group">
                {ASSIGNATION_OPTIONS.map(assignation => (
                  <AssignationRadioButton
                    key={assignation}
                    assignation={assignation}
                    isTripCfr={isTripCfr}
                    beforeToday={beforeToday}
                    setAssignValue={setAssignValue}
                    assignValue={assignValue}
                  />
                ))}
              </FlexRow>
            </Form.Item>
          </StyledRow>
          {/* CAMPOS DE CONTRATO */}
          {(() => {
            const isAdminOrAdminRO =
              roles?.includes(RoleEnum.ADMIN) || roles?.includes(RoleEnum.ADMINRO)
            const isFOB = tripTypeValue === TripTypeEnum.FOB
            const isTransference = tripTypeValue === TripTypeEnum.TRANSFERENCE
            const isManual = assignValue === 'manual'

            const shouldShowContractFields =
              isAdminOrAdminRO && (isFOB || isTransference) && isManual

            return shouldShowContractFields
          })() && (
            <>
              <StyledRow className="new-ticket-form-row">
                <FlexRow style={{ alignItems: 'start', gap: '8px', flex: 1 }}>
                  {/* <Select
                      placeholder={selectedSapItemData?.contract || t('globals.placeholder_select')}
                      value={selectedSapItemData?.contract}
                      options={[
                        {
                          label: selectedSapItemData?.contract || t('globals.placeholder_select'),
                          value: selectedSapItemData?.contract,
                        },
                      ]}
                    /> */}
                  <Form.Item
                    label={t('admin_router.admin_dashboard.settings.contract')}
                    name={CONTRACT_LINE_FIELD_NAME}
                    rules={[
                      {
                        required: tripTypeValue === TripTypeEnum.FOB,
                        message: isRequiredMsg,
                      },
                      /* ({ getFieldValue }) => ({
                        validator(_, value) {
                          const isFOB = getFieldValue('tripType') === TripTypeEnum.FOB
                          const isManual = getFieldValue('autoAssignValue') === 'manual'
                          const contractLine = !getFieldValue(CONTRACT_LINE_FIELD_NAME)

                          // Si se ha realizado una simulación exitosa, pasar la validación
                          if (hasSuccessfulSimulation) {
                            return Promise.resolve()
                          }

                          if (!contractLine && isFOB && isManual) {
                            if (!value)
                              return Promise.reject(new Error(t('Debe ingresar ticketSap')))
                            if (String(value).length !== 8) {
                              return Promise.reject(
                                new Error(t('ticketSap debe tener 8 caracteres')),
                              )
                            }
                          }
                          return Promise.resolve()
                        },
                      }), */
                    ]}
                  >
                    <Select
                      placeholder={t('globals.placeholder_select')}
                      options={contractsByCarrier?.map(
                        ({ contract, descriptionContract, companySap }) => {
                          const companyPart = companySap ? ` (${companySap})` : ''
                          return {
                            label: descriptionContract
                              ? `${contract}${companyPart} - ${descriptionContract}`
                              : `${contract}${companyPart}`,
                            value: contract,
                          }
                        },
                      )}
                      onChange={handleContractOnChange}
                    />
                  </Form.Item>
                  <Tooltip
                    title={t('admin_router.admin_dashboard.settings.search_contract_position')}
                  >
                    {/* <Button
                        type="primary"
                        icon={<SyncOutlined spin={isSimulating} />}
                        onClick={handleSearchContractPosition}
                        disabled={
                          roles?.includes(RoleEnum.ADMINRO) ||
                          !selectedCarrier ||
                          !selectedEquipmentId ||
                          !dates?.length ||
                          isSimulating
                        }
                        loading={isSimulating}
                        className="contract-search-button"
                      /> */}
                    <Button
                      type="primary"
                      icon={<SyncOutlined spin={getContractsByCarrierIsLoading} />}
                      onClick={handleSearchContractPosition}
                      disabled={
                        roles?.includes(RoleEnum.ADMINRO) ||
                        !selectedCarrier ||
                        !selectedEquipmentId ||
                        !dates?.length ||
                        getContractsByCarrierIsLoading
                      }
                      loading={getContractsByCarrierIsLoading}
                      className="contract-search-button"
                    />
                  </Tooltip>
                </FlexRow>
              </StyledRow>
              {/* TODO: CAMPOS DE CONTRATO AGREGADO */}
              <StyledRow className="new-ticket-form-row">
                <Form.Item
                  label={t('admin_router.admin_dashboard.settings.contract_line')}
                  name="sapItemId"
                  rules={[
                    {
                      required: tripTypeValue === TripTypeEnum.FOB,
                      message: isRequiredMsg,
                    },
                    /* ({ getFieldValue }) => ({
                      validator(_, value) {
                        const isFOB = getFieldValue('tripType') === TripTypeEnum.FOB
                        const isManual = getFieldValue('autoAssignValue') === 'manual'
                        const contractLine = !getFieldValue(CONTRACT_LINE_FIELD_NAME)

                        // Si se ha realizado una simulación exitosa, pasar la validación
                        if (hasSuccessfulSimulation) {
                          return Promise.resolve()
                        }

                        if (!contractLine && isFOB && isManual) {
                          if (!value) return Promise.reject(new Error(t('Debe ingresar ticketSap')))
                          if (String(value).length !== 8) {
                            return Promise.reject(new Error(t('ticketSap debe tener 8 caracteres')))
                          }
                        }
                        return Promise.resolve()
                      },
                    }), */
                  ]}
                >
                  {/* <Select
                      style={{ flex: 1 }}
                      showSearch
                      placeholder={t('globals.placeholder_select')}
                      options={alternativeOptions}
                      value={selectedSapItemId}
                      onChange={value => setSelectedSapItemId(value)}
                      disabled={!roles?.includes(RoleEnum.ADMIN)}
                      filterOption={handleFilterOptions}
                      getPopupContainer={(triggerNode: HTMLElement) =>
                        triggerNode.parentNode as HTMLElement
                      }
                    /> */}
                  <Select
                    style={{ flex: 1 }}
                    showSearch
                    placeholder={t('globals.placeholder_select')}
                    options={contractsByCarrier
                      ?.find(({ contract }) => contract === currentContract)
                      ?.contract_line.sort((a, b) => {
                        // Sort by position first, then by subPosition
                        if (a.position !== b.position) {
                          return a.position - b.position
                        }
                        return a.subPosition - b.subPosition
                      })
                      .map(({ id, description, position, subPosition }) => ({
                        label: `${position}/${subPosition} - ${description}`,
                        value: id,
                      }))}
                    //value={selectedSapItemId}
                    disabled={!roles?.includes(RoleEnum.ADMIN)}
                    filterOption={handleFilterOptions}
                    getPopupContainer={(triggerNode: HTMLElement) =>
                      triggerNode.parentNode as HTMLElement
                    }
                  />
                </Form.Item>
              </StyledRow>
              {/* TODO: CAMPOS DE CONTRATO AGREGADO */}
            </>
          )}
        </FlexCol>
        <FlexCol className="form-section">
          <StyledRow className="new-ticket-form-row comments">
            <Form.Item label={t('ticket_modal.comments')} name="comments">
              <TextArea maxLength={1200} rows={3} allowClear />
            </Form.Item>
          </StyledRow>
        </FlexCol>
      </FlexCol>
    </Form>
  )
}

export default AddTicketFormAdmin
